import React, { useEffect, useState } from 'react';
import Card from 'src/component/Card';
import Spinner from 'src/component/Spinner';
import { fetchScenesWithPreviews } from 'src/service/AbstractProductDesignGenerationCandidate';
import styles from './index.module.scss';

export interface Props {
  // Gelato product UID of the abstract product's default product. Scenes/previews
  // are rendered for this UID (OP-39539).
  defaultProductUid?: string;
  // Scene names selected for display in the PDP gallery. An empty list means the
  // product is uncurated and all scenes are shown.
  includedScenes: string[];
  // Called with the full explicit list of scenes to include after a toggle.
  onChange: (included: string[]) => void;
}

const PdpGalleryImagesCard = (
  {
    defaultProductUid,
    includedScenes,
    onChange,
  }: Props,
) => {
  const [scenes, setScenes] = useState<string[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!defaultProductUid) {
        setScenes([]);
        setPreviewUrls([]);
        return;
      }
      setIsLoading(true);
      try {
        const result = await fetchScenesWithPreviews(defaultProductUid);
        if (!cancelled) {
          setScenes(result?.scenes || []);
          setPreviewUrls(result?.previewUrls || []);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch scenes with previews:', e);
        if (!cancelled) {
          setScenes([]);
          setPreviewUrls([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [defaultProductUid]);

  // Empty selection = uncurated = everything shown.
  const isIncluded = (scene: string): boolean => includedScenes.length === 0 || includedScenes.includes(scene);

  // Build the new explicit selection (all currently-shown scenes after the toggle)
  // and hand it up. Starting from an uncurated (empty) state, the first toggle
  // materialises the full list so intent is captured explicitly.
  const handleToggle = (scene: string, checked: boolean): void => {
    const current = scenes.filter((s) => isIncluded(s));
    const next = checked
      ? Array.from(new Set([...current, scene]))
      : current.filter((s) => s !== scene);
    onChange(next);
  };

  const renderBody = () => {
    if (!defaultProductUid) {
      return <em>Select a default product page product above to manage gallery images.</em>;
    }
    if (isLoading) {
      return <Spinner size={40} />;
    }
    if (!scenes.length) {
      return <em>No preview scenes available for this product.</em>;
    }

    return (
      <>
        <p>
          Checked images are shown in the PDP gallery. Uncheck an image to hide it.
          With nothing selected, all images are shown.
        </p>
        <div className={styles.sceneGrid}>
          {scenes.map((scene, index) => {
            const included = isIncluded(scene);
            const tileClass = included
              ? styles.sceneTile
              : `${styles.sceneTile} ${styles.excluded}`;
            const previewUrl = previewUrls[index];

            return (
              <div
                key={scene}
                className={tileClass}
                onClick={() => handleToggle(scene, !included)}
              >
                {previewUrl ? (
                  <img className={styles.sceneImage} src={previewUrl} alt={scene} />
                ) : (
                  <div className={styles.sceneImage} />
                )}
                <label className={styles.sceneLabel}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={included}
                    onChange={(event) => {
                      event.stopPropagation();
                      handleToggle(scene, event.target.checked);
                    }}
                    onClick={(event) => event.stopPropagation()}
                  />
                  {scene}
                </label>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <Card header="PDP gallery images" className="mb-3">
      {renderBody()}
    </Card>
  );
};

export default PdpGalleryImagesCard;
